import { useEffect, useRef } from "react"

import useSlugExtractor from "@hooks/useSlugExtractor"
import useUser from "@hooks/useUser"

import useObjectsView from "@organisms/ObjectsView/ObjectsView.context"
import ViewBar from "@organisms/ViewBar/ViewBar"

import { ObjectsViewTabProps } from "./ObjectsViewTab.types"

export default function ObjectsViewTab(props: ObjectsViewTabProps) {
    const { id, title, phase, countEndpoint, endpointKwargs, listEndpoint, isDefaultActiveTabForPhase, hide } = props

    const { activePhase, activeTab, setActiveTab, activeTabOverride, setActivePhase } = useObjectsView()
    const { isLoading } = useUser()
    const hasSetTabFromUrl = useRef(false)

    const [tabListEndpoint] = useSlugExtractor([listEndpoint])

    const onSetActiveTab = () => {
        setActivePhase(phase)

        // Saved as route templates, so a workspace switch never reuses another company's URLs.
        setActiveTab({
            id,
            title,
            phase,
            countEndpoint,
            endpointKwargs,
            listEndpoint,
        })
    }

    const isUrlActivated = activeTabOverride === title && !hasSetTabFromUrl.current

    const isLegacyActivated = typeof activeTab === "string" && activeTab === title

    const listEndpointHasChanged =
        activeTab?.listEndpoint !== tabListEndpoint &&
        !!activeTab?.listEndpoint &&
        activeTab?.id === id &&
        activeTab?.phase === phase

    const isDefaultActivated = !!isDefaultActiveTabForPhase && activePhase === phase && activeTab?.phase !== phase

    useEffect(() => {
        if (isLoading) {
            return
        }

        const shouldActivateTab = isUrlActivated || isLegacyActivated || isDefaultActivated || listEndpointHasChanged

        if (shouldActivateTab) {
            onSetActiveTab()

            hasSetTabFromUrl.current = true
        }
    }, [
        isLoading,
        activeTabOverride,
        id,
        activeTab,
        title,
        isDefaultActiveTabForPhase,
        activePhase,
        phase,
        onSetActiveTab,
        listEndpointHasChanged,
    ])

    return (
        <ViewBar.Tab
            value={id}
            label={title}
            phase={phase}
            isSelected={activeTab?.id === id || isDefaultActivated}
            isHidden={hide}
            onSelect={onSetActiveTab}
        />
    )
}
